import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box, Paper, Tabs, Tab, Grid, Card, CardContent, Typography,
  Button, IconButton, Chip, Alert, Snackbar, Dialog, DialogTitle,
  DialogContent, DialogActions, TextField, Select, MenuItem,
  FormControl, InputLabel, Switch, FormControlLabel, Divider,
  List, ListItem, ListItemText, ListItemIcon, Badge
} from '@mui/material';
import {
  Edit as EditIcon, Visibility as ViewIcon,
  Add as AddIcon, Delete as DeleteIcon, Settings as SettingsIcon,
  Warning as WarningIcon, Person as PersonIcon, LocalHospital as HospitalIcon,
  Business as BusinessIcon, Room as RoomIcon,
  ZoomIn as ZoomInIcon, ZoomOut as ZoomOutIcon, CenterFocusStrong as CenterIcon,
  Upload as UploadIcon, Map as MapIcon
} from '@mui/icons-material';
import { zonesService } from '../../services/zonesService';
import { mapsAPI, devicesAPI, equipmentAPI, patientsAPI, staffAPI } from '../../services/api';
import MapCanvas from './MapCanvas';
import toast from 'react-hot-toast';

const InteractiveMaps = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [zones, setZones] = useState([]);
  const [entities, setEntities] = useState([]);
  const [availableMaps, setAvailableMaps] = useState([]);
  const [selectedMap, setSelectedMap] = useState(null);
  const [selectedZone, setSelectedZone] = useState(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [drawingMode, setDrawingMode] = useState('select');
  const [gateways, setGateways] = useState([]);
  const [isPositioningGateway, setIsPositioningGateway] = useState(false);
  const [selectedGateway, setSelectedGateway] = useState(null);
  const [sidebarTab, setSidebarTab] = useState(0);
  const [devices, setDevices] = useState([]);
  
  const handleDrawingModeChange = (mode) => {
    console.log('InteractiveMaps: Drawing mode changed to:', mode);
    setDrawingMode(mode);
  };
  const [mapUploadDialog, setMapUploadDialog] = useState(false);

  const [showLayers, setShowLayers] = useState({
    zones: true,
    gateways: true,
    equipment: true,
    patients: true,
    staff: true,
    barriers: true
  });
  const [alerts, setAlerts] = useState([]);


  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Dialogs
  const [zoneDialog, setZoneDialog] = useState(false);
  const [alertDialog, setAlertDialog] = useState(false);
  
  // Canvas refs
  const canvasRef = useRef(null);
  const [canvasScale, setCanvasScale] = useState(1);
  const [canvasOffset, setCanvasOffset] = useState({ x: 0, y: 0 });
  
  // Real-time updates
  const [realTimeEnabled, setRealTimeEnabled] = useState(true);
  const intervalRef = useRef(null);

  // Load initial data
  useEffect(() => {
    loadAvailableMaps();
  }, []);
  
  useEffect(() => {
    if (selectedMap) {
      loadZones();
      loadEntities();
      loadAlerts();
      loadGateways();
      loadDevices();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedMap]);

  // Real-time updates
  useEffect(() => {
    if (realTimeEnabled) {
      intervalRef.current = setInterval(() => {
        loadEntities();
        loadAlerts();
        loadDevices();
      }, 5000);
    } else {
      clearInterval(intervalRef.current);
    }
    
    return () => clearInterval(intervalRef.current);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [realTimeEnabled]);

  const loadAvailableMaps = async () => {
    try {
      const response = await mapsAPI.getAll();
      const mapsData = Array.isArray(response?.data?.data) ? response.data.data : 
                      Array.isArray(response?.data) ? response.data : [];
      setAvailableMaps(mapsData);
      
      // Auto-select first map if available
      if (mapsData.length > 0 && !selectedMap) {
        setSelectedMap(mapsData[0]);
      }
    } catch (error) {
      console.warn('Maps API failed:', error.message);
      // Fallback to mock data
      const mockMaps = [
        { id: 1, area: 'terreo-emergencia', name: 'Térreo - Emergência', file: 'terreo.png', url: 'https://via.placeholder.com/800x600/f0f0f0/666?text=Planta+Terreo' },
        { id: 2, area: 'primeiro-uti', name: '1º Andar - UTI', file: 'primeiro.png', url: 'https://via.placeholder.com/800x600/e8f5e8/333?text=Planta+UTI' }
      ];
      setAvailableMaps(mockMaps);
      if (!selectedMap) setSelectedMap(mockMaps[0]);
    }
  };

  const loadZones = async () => {
    if (!selectedMap) return;
    try {
      const response = await zonesService.getZones({ map_id: selectedMap.id });
      setZones(response.zones || []);
    } catch (err) {
      setError('Erro ao carregar zonas');
    }
  };

  const loadEntities = async () => {
    if (!selectedMap) return;
    try {
      const [patientsRes, staffRes] = await Promise.all([
        patientsAPI.getAll().catch(() => ({ data: { data: [] } })),
        staffAPI.getAll().catch(() => ({ data: { data: [] } }))
      ]);
      
      const patientsData = patientsRes.data?.data || [];
      const staffData = staffRes.data?.data || [];
      
      const patients = patientsData
        .filter(p => p.area_id === selectedMap.id && (p.x != null || p.location_x != null))
        .map(p => ({
          id: p.id,
          entity_id: p.medical_record_number || p.external_patient_id || 'Paciente',
          entity_type: 'patient',
          area_id: p.area_id,
          x: parseFloat(p.location_x || p.x || 0),
          y: parseFloat(p.location_y || p.y || 0),
          current_x: parseFloat(p.location_x || p.x || 0),
          current_y: parseFloat(p.location_y || p.y || 0),
          status: p.tracking_status || 'active',
          display_name: p.medical_record_number || p.external_patient_id
        }));
      
      const staff = staffData
        .filter(s => s.area_id === selectedMap.id && (s.current_x != null || s.location_x != null))
        .map(s => ({
          id: s.id,
          entity_id: s.full_name || s.staff_code,
          entity_type: 'staff',
          area_id: s.area_id,
          x: parseFloat(s.location_x || s.current_x || 0),
          y: parseFloat(s.location_y || s.current_y || 0),
          current_x: parseFloat(s.location_x || s.current_x || 0),
          current_y: parseFloat(s.location_y || s.current_y || 0),
          status: s.employment_status || 'active',
          display_name: s.full_name
        }));
      
      setEntities([...patients, ...staff]);
    } catch (err) {
      console.error('Erro ao carregar entidades:', err);
      setEntities([]);
    }
  };

  const loadAlerts = async () => {
    try {
      const response = await zonesService.getActiveAlerts();
      setAlerts(response.alerts || []);
    } catch (err) {
      console.error('Erro ao carregar alertas:', err);
    }
  };

  const loadGateways = async () => {
    if (!selectedMap) return;
    try {
      const response = await devicesAPI.getAll();
      const devicesData = Array.isArray(response?.data?.data) ? response.data.data : 
                         Array.isArray(response?.data) ? response.data : [];
      
      const allMapGateways = devicesData
        .filter(device => device.type === 'gateway' && device.area_id === selectedMap.id)
        .map(device => ({
          id: device.id,
          name: device.name,
          type: device.type,
          area_id: device.area_id,
          x: parseFloat(device.location_x) || 0,
          y: parseFloat(device.location_y) || 0,
          status: device.status || 'offline'
        }));
      
      setGateways(allMapGateways);
    } catch (err) {
      console.error('Erro ao carregar gateways:', err);
      setGateways([]);
    }
  };

  const loadDevices = async () => {
    if (!selectedMap) return;
    try {
      const response = await equipmentAPI.getWithLocation();
      const equipmentData = response.data?.data || [];
      
      const allDevices = equipmentData
        .filter(eq => eq.device_id && eq.location_x && eq.location_y && eq.area_id === selectedMap.id)
        .map(eq => ({
          id: eq.id,
          name: eq.equipment_name,
          ble_id: eq.ble_id,
          type: 'equipment',
          area_id: eq.area_id,
          x: parseFloat(eq.location_x),
          y: parseFloat(eq.location_y),
          current_x: parseFloat(eq.location_x),
          current_y: parseFloat(eq.location_y),
          status: eq.status || 'offline',
          battery_level: eq.battery_level,
          last_seen: eq.location_timestamp,
          equipment_name: eq.equipment_name,
          equipment_type: eq.equipment_type,
          display_name: eq.equipment_name,
          entity_id: eq.equipment_name,
          entity_type: 'equipment'
        }));
      
      setDevices(allDevices);
    } catch (error) {
      console.warn('Equipment API failed:', error.message);
      setDevices([]);
    }
  };



  // Canvas drawing functions
  const drawGrid = useCallback((ctx) => {
    const gridSize = 20 * canvasScale;
    ctx.strokeStyle = '#e0e0e0';
    ctx.lineWidth = 1;
    
    for (let x = 0; x < ctx.canvas.width; x += gridSize) {
      ctx.beginPath();
      ctx.moveTo(x, 0);
      ctx.lineTo(x, ctx.canvas.height);
      ctx.stroke();
    }
    
    for (let y = 0; y < ctx.canvas.height; y += gridSize) {
      ctx.beginPath();
      ctx.moveTo(0, y);
      ctx.lineTo(ctx.canvas.width, y);
      ctx.stroke();
    }
  }, [canvasScale]);

  const drawZone = useCallback((ctx, zone) => {
    const coords = zone.coordinates;
    if (!coords) return;
    
    ctx.save();
    ctx.scale(canvasScale, canvasScale);
    ctx.translate(canvasOffset.x, canvasOffset.y);
    
    const colors = {
      room: '#4CAF50',
      bed: '#2196F3',
      ward: '#FF9800',
      department: '#9C27B0',
      restricted: '#F44336',
      corridor: '#607D8B',
      elevator: '#795548',
      exit: '#E91E63'
    };
    
    ctx.fillStyle = colors[zone.zone_type] || '#9E9E9E';
    ctx.globalAlpha = selectedZone?.id === zone.id ? 0.7 : 0.3;
    
    if (coords.type === 'rectangle') {
      ctx.fillRect(coords.x, coords.y, coords.width, coords.height);
      ctx.strokeStyle = colors[zone.zone_type];
      ctx.lineWidth = 2;
      ctx.strokeRect(coords.x, coords.y, coords.width, coords.height);
    }
    
    ctx.globalAlpha = 1;
    ctx.fillStyle = '#000';
    ctx.font = '12px Arial';
    ctx.textAlign = 'center';
    const centerX = coords.center_point?.x || coords.x || 0;
    const centerY = coords.center_point?.y || coords.y || 0;
    ctx.fillText(zone.zone_code, centerX, centerY);
    
    ctx.restore();
  }, [canvasScale, canvasOffset, selectedZone]);

  const drawEntity = useCallback((ctx, entity) => {
    if (!showLayers[entity.entity_type]) return;
    
    ctx.save();
    ctx.scale(canvasScale, canvasScale);
    ctx.translate(canvasOffset.x, canvasOffset.y);
    
    const x = entity.current_x || 0;
    const y = entity.current_y || 0;
    
    const colors = {
      equipment: '#FF5722',
      patient: '#E91E63',
      staff: '#3F51B5'
    };
    
    ctx.fillStyle = colors[entity.entity_type];
    ctx.beginPath();
    ctx.arc(x, y, 6, 0, 2 * Math.PI);
    ctx.fill();
    
    ctx.restore();
  }, [canvasScale, canvasOffset, showLayers]);

  const drawCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    
    drawGrid(ctx);
    zones.forEach(zone => drawZone(ctx, zone));
    
    if (activeTab === 1) {
      entities.forEach(entity => drawEntity(ctx, entity));
    }
  }, [zones, entities, activeTab, drawGrid, drawZone, drawEntity]);

  // Canvas event handlers
  const handleCanvasClick = (event) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const x = (event.clientX - rect.left) / canvasScale - canvasOffset.x;
    const y = (event.clientY - rect.top) / canvasScale - canvasOffset.y;
    
    if (activeTab === 0 && isDrawing) {
      // Drawing mode - create new zone
      handleCreateZone(x, y);
    } else {
      // Selection mode - select zone or entity
      const clickedZone = findZoneAtPoint(x, y);
      setSelectedZone(clickedZone);
    }
  };

  const findZoneAtPoint = (x, y) => {
    return zones.find(zone => {
      const coords = zone.coordinates;
      if (!coords) return false;
      
      if (coords.type === 'rectangle') {
        return x >= coords.x && x <= coords.x + coords.width &&
               y >= coords.y && y <= coords.y + coords.height;
      } else if (coords.type === 'circle') {
        const distance = Math.sqrt((x - coords.x) ** 2 + (y - coords.y) ** 2);
        return distance <= coords.radius;
      } else if (coords.type === 'polygon') {
        return zonesService.isPointInPolygon({ x, y }, coords.points);
      }
      return false;
    });
  };

  const handleCreateZone = (zoneData) => {
    console.log('InteractiveMaps: handleCreateZone called with:', zoneData);
    if (!selectedMap) {
      toast.error('Selecione um mapa primeiro');
      return;
    }
    
    if (selectedZone && selectedZone.id) {
      // Editando zona existente - atualizar coordenadas
      const updatedZone = {
        ...selectedZone,
        coordinates: zoneData.coordinates,
        area_m2: calculateArea(zoneData.coordinates),
        center_point: zoneData.coordinates.center_point
      };
      console.log('InteractiveMaps: Updating existing zone coordinates:', updatedZone);
      setSelectedZone(updatedZone);
      setZoneDialog(true);
    } else {
      // Criando nova zona
      const newZone = {
        zone_code: `ZONE_${Date.now()}`,
        zone_name: 'Nova Zona',
        zone_type: 'room',
        map_id: selectedMap.id,
        map_area: selectedMap.area_name || selectedMap.area || selectedMap.name,
        coordinates: zoneData.coordinates,
        area_m2: calculateArea(zoneData.coordinates),
        center_point: zoneData.coordinates.center_point
      };
      console.log('InteractiveMaps: Created newZone:', newZone);
      setSelectedZone(newZone);
      setZoneDialog(true);
    }
  };
  
  const calculateArea = (coords) => {
    if (!coords) return 0;
    
    if (coords.type === 'rectangle') {
      return Math.round(coords.width * coords.height);
    } else if (coords.type === 'circle') {
      return Math.round(Math.PI * coords.radius * coords.radius);
    } else if (coords.type === 'polygon' && coords.points) {
      return Math.round(zonesService.calculatePolygonArea(coords.points));
    }
    return 0;
  };

  const handleSaveZone = async (zoneData) => {
    console.log('InteractiveMaps: handleSaveZone called with:', zoneData);
    try {
      if (selectedZone.id) {
        console.log('InteractiveMaps: Updating existing zone');
        await zonesService.updateZone(selectedZone.id, zoneData);
        setSuccess('Zona atualizada com sucesso');
      } else {
        console.log('InteractiveMaps: Creating new zone');
        const result = await zonesService.createZone(zoneData);
        console.log('InteractiveMaps: Zone creation result:', result);
        setSuccess('Zona criada com sucesso');
      }
      setZoneDialog(false);
      setSelectedZone(null);
      setDrawingMode('select'); // Reset drawing mode after saving
      loadZones();
    } catch (err) {
      console.error('InteractiveMaps: Error saving zone:', err);
      setError('Erro ao salvar zona: ' + err.message);
    }
  };

  const handleDeleteZone = async () => {
    if (!selectedZone?.id) return;
    
    try {
      await zonesService.deleteZone(selectedZone.id);
      setSuccess('Zona excluída com sucesso');
      setSelectedZone(null);
      setDrawingMode('select'); // Reset drawing mode after deleting
      loadZones();
    } catch (err) {
      setError('Erro ao excluir zona');
    }
  };

  const handleZoneSelect = (zone) => {
    setSelectedZone(zone);
    if (!zone) {
      setDrawingMode('select'); // Reset drawing mode when no zone is selected
    }
  };

  const handleGatewayClick = (gateway) => {
    if (isPositioningGateway) {
      setSelectedGateway(gateway);
      toast(`Clique no mapa para posicionar ${gateway.name}`, { icon: 'ℹ️' });
    }
  };

  const handleGatewayPosition = async (x, y) => {
    if (!selectedGateway) {
      console.log('No gateway selected');
      return;
    }
    
    if (!selectedMap) {
      toast.error('Selecione um mapa primeiro');
      return;
    }
    
    console.log('Positioning gateway:', selectedGateway.name, 'at', x, y);
    console.log('Selected map:', selectedMap);
    
    try {
      const updateData = {
        location_x: Math.round(x),
        location_y: Math.round(y),
        location: selectedMap.id // Set area_id to map id when positioning
      };
      
      console.log('Update data:', updateData);
      
      await devicesAPI.update(selectedGateway.id, updateData);
      
      setGateways(prev => prev.map(gateway => 
        gateway.id === selectedGateway.id 
          ? { ...gateway, x: Math.round(x), y: Math.round(y), area_id: selectedMap.id || selectedMap.area }
          : gateway
      ));
      
      toast.success(`${selectedGateway.name} posicionado em (${Math.round(x)}, ${Math.round(y)})!`);
      setIsPositioningGateway(false);
      setSelectedGateway(null);
      loadGateways(); // Reload to confirm
    } catch (error) {
      console.error('Error positioning gateway:', error);
      console.error('Error response:', error.response?.data);
      toast.error('Erro ao posicionar gateway: ' + (error.response?.data?.message || error.message));
    }
  };

  // Canvas controls
  const handleZoomIn = () => setCanvasScale(prev => Math.min(prev * 1.2, 3));
  const handleZoomOut = () => setCanvasScale(prev => Math.max(prev / 1.2, 0.3));
  const handleCenter = () => {
    setCanvasScale(1);
    setCanvasOffset({ x: 0, y: 0 });
  };

  // Redraw canvas when data changes
  useEffect(() => {
    drawCanvas();
  }, [drawCanvas]);

  const TabPanel = ({ children, value, index }) => (
    <div hidden={value !== index}>
      {value === index && children}
    </div>
  );

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Mapas Interativos
      </Typography>
      
      <Paper sx={{ mb: 3 }}>
        <Tabs value={activeTab} onChange={(e, v) => setActiveTab(v)}>
          <Tab icon={<EditIcon />} label="Editor de Zonas" />
          <Tab icon={<ViewIcon />} label="Visualização Tempo Real" />
          <Tab icon={<SettingsIcon />} label="Configurações" />
        </Tabs>
      </Paper>

      {/* Map Selection and Controls */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={4}>
            <FormControl fullWidth size="small">
              <InputLabel>Mapa Ativo</InputLabel>
              <Select
                value={selectedMap?.id || ''}
                onChange={(e) => {
                  const map = availableMaps.find(m => m.id === e.target.value);
                  setSelectedMap(map);
                }}
                label="Mapa Ativo"
              >
                {availableMaps.map(map => (
                  <MenuItem key={map.id} value={map.id}>{map.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          
          {/* Layer Controls */}
          <Grid item xs={12} sm={8}>
            <Typography variant="caption" sx={{ display: 'block', mb: 1 }}>
              Layers Visíveis:
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              <FormControlLabel
                control={
                  <Switch
                    size="small"
                    checked={showLayers.zones}
                    onChange={(e) => setShowLayers(prev => ({ ...prev, zones: e.target.checked }))}
                    sx={{
                      '& .MuiSwitch-switchBase.Mui-checked': {
                        color: '#4CAF50',
                        '&:hover': { bgcolor: 'rgba(76, 175, 80, 0.08)' }
                      },
                      '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                        bgcolor: '#4CAF50'
                      }
                    }}
                  />
                }
                label="Zonas"
                sx={{ mr: 2 }}
              />
              <FormControlLabel
                control={
                  <Switch
                    size="small"
                    checked={showLayers.gateways}
                    onChange={(e) => setShowLayers(prev => ({ ...prev, gateways: e.target.checked }))}
                    sx={{
                      '& .MuiSwitch-switchBase.Mui-checked': {
                        color: '#2196F3',
                        '&:hover': { bgcolor: 'rgba(33, 150, 243, 0.08)' }
                      },
                      '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                        bgcolor: '#2196F3'
                      }
                    }}
                  />
                }
                label="Gateways"
                sx={{ mr: 2 }}
              />
              <FormControlLabel
                control={
                  <Switch
                    size="small"
                    checked={showLayers.equipment}
                    onChange={(e) => setShowLayers(prev => ({ ...prev, equipment: e.target.checked }))}
                    sx={{
                      '& .MuiSwitch-switchBase.Mui-checked': {
                        color: '#FF5722',
                        '&:hover': { bgcolor: 'rgba(255, 87, 34, 0.08)' }
                      },
                      '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                        bgcolor: '#FF5722'
                      }
                    }}
                  />
                }
                label="Equipamentos"
                sx={{ mr: 2 }}
              />
              <FormControlLabel
                control={
                  <Switch
                    size="small"
                    checked={showLayers.patients}
                    onChange={(e) => setShowLayers(prev => ({ ...prev, patients: e.target.checked }))}
                    sx={{
                      '& .MuiSwitch-switchBase.Mui-checked': {
                        color: '#E91E63',
                        '&:hover': { bgcolor: 'rgba(233, 30, 99, 0.08)' }
                      },
                      '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                        bgcolor: '#E91E63'
                      }
                    }}
                  />
                }
                label="Pacientes"
                sx={{ mr: 2 }}
              />
              <FormControlLabel
                control={
                  <Switch
                    size="small"
                    checked={showLayers.staff}
                    onChange={(e) => setShowLayers(prev => ({ ...prev, staff: e.target.checked }))}
                    sx={{
                      '& .MuiSwitch-switchBase.Mui-checked': {
                        color: '#3F51B5',
                        '&:hover': { bgcolor: 'rgba(63, 81, 181, 0.08)' }
                      },
                      '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                        bgcolor: '#3F51B5'
                      }
                    }}
                  />
                }
                label="Funcionários"
                sx={{ mr: 2 }}
              />
              <FormControlLabel
                control={
                  <Switch
                    size="small"
                    checked={showLayers.barriers}
                    onChange={(e) => setShowLayers(prev => ({ ...prev, barriers: e.target.checked }))}
                    sx={{
                      '& .MuiSwitch-switchBase.Mui-checked': {
                        color: '#9C27B0',
                        '&:hover': { bgcolor: 'rgba(156, 39, 176, 0.08)' }
                      },
                      '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                        bgcolor: '#9C27B0'
                      }
                    }}
                  />
                }
                label="Barreiras"
              />
            </Box>
          </Grid>
          
          <Grid item>
            <Button
              variant="outlined"
              startIcon={<UploadIcon />}
              onClick={() => setMapUploadDialog(true)}
              size="small"
            >
              Gerenciar Mapas
            </Button>
          </Grid>
          

          

          
          <Grid item>
            <IconButton onClick={handleZoomIn} title="Zoom In">
              <ZoomInIcon />
            </IconButton>
            <IconButton onClick={handleZoomOut} title="Zoom Out">
              <ZoomOutIcon />
            </IconButton>
            <IconButton onClick={handleCenter} title="Centralizar">
              <CenterIcon />
            </IconButton>
          </Grid>
          
          <Grid item sx={{ ml: 'auto' }}>
            <Badge badgeContent={alerts.length} color="error">
              <IconButton onClick={() => setAlertDialog(true)}>
                <WarningIcon />
              </IconButton>
            </Badge>
          </Grid>
        </Grid>
      </Paper>

      <Grid container spacing={3}>
        {/* Main Canvas Area */}
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 2, height: 600 }}>
            {!selectedMap ? (
              <Box sx={{ 
                display: 'flex', 
                flexDirection: 'column', 
                alignItems: 'center', 
                justifyContent: 'center', 
                height: '100%',
                color: 'text.secondary'
              }}>
                <MapIcon sx={{ fontSize: 64, mb: 2 }} />
                <Typography variant="h6" gutterBottom>
                  Nenhum mapa selecionado
                </Typography>
                <Typography variant="body2" sx={{ mb: 2 }}>
                  Selecione um mapa existente ou crie um novo
                </Typography>
                <Button
                  variant="contained"
                  startIcon={<UploadIcon />}
                  onClick={() => setMapUploadDialog(true)}
                >
                  Criar Primeiro Mapa
                </Button>
              </Box>
            ) : (
              <>
                <TabPanel value={activeTab} index={0}>
                  {/* Editor Mode */}
                  {selectedZone && (
                    <Box sx={{ mb: 2 }}>
                      <Button
                        variant="outlined"
                        onClick={() => setZoneDialog(true)}
                        startIcon={<EditIcon />}
                        sx={{ mr: 1 }}
                      >
                        Editar
                      </Button>
                      <Button
                        variant="outlined"
                        color="error"
                        onClick={handleDeleteZone}
                        startIcon={<DeleteIcon />}
                      >
                        Excluir
                      </Button>
                    </Box>
                  )}
                </TabPanel>
                
                <TabPanel value={activeTab} index={1}>
                  {/* View Mode - Controls moved to top bar */}
                </TabPanel>
                

                
                {/* MapCanvas sempre visível */}
                <MapCanvas
                  zones={zones}
                  entities={[
                    ...entities,
                    ...devices
                  ]}
                  gateways={gateways}
                  selectedZone={selectedZone}
                  selectedGateway={selectedGateway}
                  onZoneSelect={handleZoneSelect}
                  onZoneCreate={handleCreateZone}
                  onZoneUpdate={loadZones}
                  onGatewayClick={handleGatewayClick}
                  onGatewayPosition={handleGatewayPosition}
                  drawingMode={drawingMode}
                  onDrawingModeChange={handleDrawingModeChange}
                  showLayers={showLayers}
                  floorPlan={selectedMap?.url}
                  scale={canvasScale}
                  offset={canvasOffset}
                  isPositioningGateway={isPositioningGateway}
                />
              </>
            )}
          </Paper>
        </Grid>

        {/* Side Panel */}
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 2, height: 600, overflow: 'auto' }}>
            {selectedZone ? (
              <Card sx={{ mb: 2 }}>
                <CardContent>
                  <Typography variant="subtitle1" gutterBottom>
                    {selectedZone.zone_name || 'Zona Selecionada'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Código: {selectedZone.zone_code}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Tipo: {selectedZone.zone_type}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Mapa: {selectedZone.map_area}
                  </Typography>
                </CardContent>
              </Card>
            ) : (
              <Alert severity="info" sx={{ mb: 2 }}>
                Clique em um item para ver detalhes
              </Alert>
            )}
            
            {/* Sidebar Tabs */}
            {(() => {
              const availableTabs = [];
              const tabMapping = {};
              let tabIndex = 0;
              
              if (showLayers.zones) { tabMapping.zones = tabIndex; availableTabs.push('zones'); tabIndex++; }
              if (showLayers.gateways) { tabMapping.gateways = tabIndex; availableTabs.push('gateways'); tabIndex++; }
              if (showLayers.equipment) { tabMapping.equipment = tabIndex; availableTabs.push('equipment'); tabIndex++; }
              if (showLayers.patients) { tabMapping.patients = tabIndex; availableTabs.push('patients'); tabIndex++; }
              if (showLayers.staff) { tabMapping.staff = tabIndex; availableTabs.push('staff'); tabIndex++; }
              if (showLayers.barriers) { tabMapping.barriers = tabIndex; availableTabs.push('barriers'); tabIndex++; }
              
              const currentTab = availableTabs[sidebarTab] || availableTabs[0];
              
              return (
                <>
                  <Paper sx={{ mb: 2 }}>
                    <Tabs 
                      value={Math.min(sidebarTab, availableTabs.length - 1)} 
                      onChange={(e, v) => setSidebarTab(v)}
                      variant="scrollable"
                      scrollButtons="auto"
                      sx={{ minHeight: 40 }}
                    >
                      {availableTabs.map((tab, index) => (
                        <Tab key={tab} label={{
                          zones: 'Zonas',
                          gateways: 'Gateways', 
                          equipment: 'Equipamentos',
                          patients: 'Pacientes',
                          staff: 'Funcionários',
                          barriers: 'Barreiras'
                        }[tab]} sx={{ minHeight: 40, py: 1 }} />
                      ))}
                    </Tabs>
                  </Paper>
                  
                  {/* Tab Content */}
                  {currentTab === 'zones' && (
                    <Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                        <Typography variant="h6">Zonas ({zones.length})</Typography>
                        <Button
                          size="small"
                          variant="contained"
                          startIcon={<AddIcon />}
                          onClick={() => {
                            setSelectedZone(null);
                            setDrawingMode('rectangle');
                          }}
                        >
                          Nova Zona
                        </Button>
                      </Box>
                      <List dense>
                        {zones.map((zone) => (
                          <ListItem
                            key={zone.id}
                            button
                            selected={selectedZone?.id === zone.id}
                            onClick={() => handleZoneSelect(zone)}
                            secondaryAction={
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleZoneSelect(zone);
                                  setZoneDialog(true);
                                }}
                              >
                                <EditIcon />
                              </IconButton>
                            }
                          >
                            <ListItemIcon>
                              <RoomIcon />
                            </ListItemIcon>
                            <ListItemText
                              primary={zone.zone_name}
                              secondary={`${zone.zone_code} - ${zone.zone_type}`}
                            />
                          </ListItem>
                        ))}
                      </List>
                    </Box>
                  )}
                  
                  {currentTab === 'gateways' && (
                    <Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                        <Typography variant="h6">Gateways ({gateways.length})</Typography>
                        <Button
                          size="small"
                          variant={isPositioningGateway ? 'contained' : 'outlined'}
                          color={isPositioningGateway ? 'secondary' : 'primary'}
                          onClick={() => {
                            setIsPositioningGateway(!isPositioningGateway);
                            setSelectedGateway(null);
                            if (!isPositioningGateway) {
                              setDrawingMode('select');
                              setSelectedZone(null);
                            }
                          }}
                          disabled={!selectedMap}
                        >
                          {isPositioningGateway ? 'Cancelar' : 'Posicionar'}
                        </Button>
                      </Box>
                      <List dense>
                        {gateways.map((gateway) => (
                          <ListItem
                            key={gateway.id}
                            button
                            selected={selectedGateway?.id === gateway.id}
                            onClick={() => {
                              if (isPositioningGateway) {
                                handleGatewayClick(gateway);
                              }
                            }}
                          >
                            <ListItemIcon>
                              <Box sx={{ 
                                width: 16, 
                                height: 16, 
                                bgcolor: gateway.status === 'online' ? '#2196F3' : '#F44336',
                                borderRadius: '20%'
                              }} />
                            </ListItemIcon>
                            <ListItemText
                              primary={gateway.name}
                              secondary={`Posição: (${gateway.x}, ${gateway.y}) - ${gateway.status}`}
                            />
                          </ListItem>
                        ))}
                      </List>
                    </Box>
                  )}
                  
                  {currentTab === 'equipment' && (
                    <Box>
                      <Typography variant="h6" gutterBottom>
                        Equipamentos ({devices.length})
                      </Typography>
                      <List dense>
                        {devices.map((equipment) => (
                          <ListItem key={`device-${equipment.id}`}>
                            <ListItemIcon>
                              <Box sx={{ width: 12, height: 12, bgcolor: '#FF5722', borderRadius: '50%' }} />
                            </ListItemIcon>
                            <ListItemText
                              primary={equipment.display_name}
                              secondary={`Posição: (${equipment.x || 0}, ${equipment.y || 0}) - ${equipment.status}`}
                            />
                          </ListItem>
                        ))}
                      </List>
                    </Box>
                  )}
                  
                  {currentTab === 'patients' && (
                    <Box>
                      <Typography variant="h6" gutterBottom>
                        Pacientes ({entities.filter(e => e.entity_type === 'patient').length})
                      </Typography>
                      <List dense>
                        {entities.filter(e => e.entity_type === 'patient').map((patient) => (
                          <ListItem key={patient.id}>
                            <ListItemIcon>
                              <Box sx={{ width: 12, height: 12, bgcolor: '#E91E63', borderRadius: '50%' }} />
                            </ListItemIcon>
                            <ListItemText
                              primary={patient.entity_id}
                              secondary={`Posição: (${patient.current_x || 0}, ${patient.current_y || 0}) - ${patient.status}`}
                            />
                          </ListItem>
                        ))}
                      </List>
                    </Box>
                  )}
                  
                  {currentTab === 'staff' && (
                    <Box>
                      <Typography variant="h6" gutterBottom>
                        Funcionários ({entities.filter(e => e.entity_type === 'staff').length})
                      </Typography>
                      <List dense>
                        {entities.filter(e => e.entity_type === 'staff').map((staff) => (
                          <ListItem key={staff.id}>
                            <ListItemIcon>
                              <Box sx={{ width: 12, height: 12, bgcolor: '#3F51B5', borderRadius: '50%' }} />
                            </ListItemIcon>
                            <ListItemText
                              primary={staff.entity_id}
                              secondary={`Posição: (${staff.current_x || 0}, ${staff.current_y || 0}) - ${staff.status}`}
                            />
                          </ListItem>
                        ))}
                      </List>
                    </Box>
                  )}
                  
                  {currentTab === 'barriers' && (
                    <Box>
                      <Typography variant="h6" gutterBottom>
                        Barreiras (0)
                      </Typography>
                      <Alert severity="info">
                        Funcionalidade em desenvolvimento
                      </Alert>
                    </Box>
                  )}
                </>
              );
            })()}
          </Paper>
        </Grid>
      </Grid>

      {/* Zone Dialog */}
      <ZoneDialog
        open={zoneDialog}
        zone={selectedZone}
        onClose={() => {
          setZoneDialog(false);
          if (!selectedZone?.id) {
            // If creating a new zone and dialog is closed, reset drawing mode
            setDrawingMode('select');
          }
        }}
        onSave={handleSaveZone}
      />

      {/* Map Upload Dialog */}
      <MapUploadDialog
        open={mapUploadDialog}
        maps={availableMaps}
        onClose={() => setMapUploadDialog(false)}
        onMapCreated={(newMap) => {
          setAvailableMaps(prev => [...prev, newMap]);
          setSelectedMap(newMap);
          toast.success(`Mapa "${newMap.name}" criado com sucesso!`);
        }}
        onMapDeleted={(mapId) => {
          setAvailableMaps(prev => prev.filter(m => m.id !== mapId));
          if (selectedMap?.id === mapId) {
            const remaining = availableMaps.filter(m => m.id !== mapId);
            setSelectedMap(remaining.length > 0 ? remaining[0] : null);
          }
          toast.success('Mapa removido com sucesso!');
        }}
      />

      {/* Alerts Dialog */}
      <AlertsDialog
        open={alertDialog}
        alerts={alerts}
        onClose={() => setAlertDialog(false)}
      />

      {/* Snackbars */}
      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={() => setError('')}
      >
        <Alert severity="error" onClose={() => setError('')}>
          {error}
        </Alert>
      </Snackbar>

      <Snackbar
        open={!!success}
        autoHideDuration={4000}
        onClose={() => setSuccess('')}
      >
        <Alert severity="success" onClose={() => setSuccess('')}>
          {success}
        </Alert>
      </Snackbar>
    </Box>
  );
};

// Zone Dialog Component
const ZoneDialog = ({ open, zone, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    zone_code: '',
    zone_name: '',
    zone_type: 'room',
    map_id: '',
    map_area: '',
    access_level: 'public',
    max_occupancy: '',
    description: ''
  });

  useEffect(() => {
    if (zone) {
      setFormData({
        zone_code: zone.zone_code || '',
        zone_name: zone.zone_name || '',
        zone_type: zone.zone_type || 'room',
        map_id: zone.map_id || '',
        map_area: zone.map_area || '',
        access_level: zone.access_level || 'public',
        max_occupancy: zone.max_occupancy || '',
        description: zone.description || ''
      });
    }
  }, [zone]);

  const handleSubmit = () => {
    const zoneData = {
      ...formData,
      coordinates: zone?.coordinates
    };
    onSave(zoneData);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {zone?.id ? 'Editar Zona' : 'Nova Zona'}
      </DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid item xs={6}>
            <TextField
              fullWidth
              label="Código da Zona"
              value={formData.zone_code}
              onChange={(e) => setFormData(prev => ({
                ...prev,
                zone_code: e.target.value
              }))}
            />
          </Grid>
          <Grid item xs={6}>
            <FormControl fullWidth>
              <InputLabel>Tipo</InputLabel>
              <Select
                value={formData.zone_type}
                onChange={(e) => setFormData(prev => ({
                  ...prev,
                  zone_type: e.target.value
                }))}
                label="Tipo"
              >
                <MenuItem value="room">Quarto</MenuItem>
                <MenuItem value="bed">Leito</MenuItem>
                <MenuItem value="ward">Ala</MenuItem>
                <MenuItem value="department">Departamento</MenuItem>
                <MenuItem value="restricted">Restrita</MenuItem>
                <MenuItem value="corridor">Corredor</MenuItem>
                <MenuItem value="elevator">Elevador</MenuItem>
                <MenuItem value="exit">Saída</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Nome da Zona"
              value={formData.zone_name}
              onChange={(e) => setFormData(prev => ({
                ...prev,
                zone_name: e.target.value
              }))}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              fullWidth
              label="Área do Mapa"
              value={formData.map_area}
              onChange={(e) => setFormData(prev => ({
                ...prev,
                map_area: e.target.value
              }))}
              disabled
              helperText="Definido automaticamente pelo mapa selecionado"
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              fullWidth
              type="number"
              label="Capacidade Máxima"
              value={formData.max_occupancy}
              onChange={(e) => setFormData(prev => ({
                ...prev,
                max_occupancy: e.target.value
              }))}
            />
          </Grid>
          <Grid item xs={12}>
            <FormControl fullWidth>
              <InputLabel>Nível de Acesso</InputLabel>
              <Select
                value={formData.access_level}
                onChange={(e) => setFormData(prev => ({
                  ...prev,
                  access_level: e.target.value
                }))}
                label="Nível de Acesso"
              >
                <MenuItem value="public">Público</MenuItem>
                <MenuItem value="restricted">Restrito</MenuItem>
                <MenuItem value="private">Privado</MenuItem>
                <MenuItem value="emergency">Emergência</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              multiline
              rows={3}
              label="Descrição"
              value={formData.description}
              onChange={(e) => setFormData(prev => ({
                ...prev,
                description: e.target.value
              }))}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancelar</Button>
        <Button onClick={handleSubmit} variant="contained">
          Salvar
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// Alerts Dialog Component
const AlertsDialog = ({ open, alerts, onClose }) => (
  <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
    <DialogTitle>Alertas Ativos</DialogTitle>
    <DialogContent>
      {alerts.length === 0 ? (
        <Alert severity="success">Nenhum alerta ativo</Alert>
      ) : (
        <List>
          {alerts.map((alert) => (
            <ListItem key={alert.id}>
              <ListItemIcon>
                <WarningIcon color={alert.severity === 'critical' ? 'error' : 'warning'} />
              </ListItemIcon>
              <ListItemText
                primary={alert.title}
                secondary={`${alert.description} - ${alert.zone_id}`}
              />
            </ListItem>
          ))}
        </List>
      )}
    </DialogContent>
    <DialogActions>
      <Button onClick={onClose}>Fechar</Button>
    </DialogActions>
  </Dialog>
);

// Map Upload Dialog Component
const MapUploadDialog = ({ open, maps, onClose, onMapCreated, onMapDeleted }) => {
  const [newMapName, setNewMapName] = useState('');
  const [uploading, setUploading] = useState(false);

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file || !newMapName.trim()) {
      toast.error('Nome do mapa e arquivo são obrigatórios');
      return;
    }

    setUploading(true);
    try {
      const areaId = newMapName.toLowerCase()
        .replace(/[^a-z0-9\s-]/g, '')
        .replace(/\s+/g, '-')
        .replace(/-+/g, '-')
        .replace(/^-|-$/g, '') || `map-${Date.now()}`;

      // Step 1: Get presigned URL
      const uploadUrlResponse = await mapsAPI.create({
        requestType: 'upload-url',
        area: areaId,
        name: newMapName.trim(),
        fileName: file.name,
        contentType: file.type
      });

      const { uploadUrl } = uploadUrlResponse.data.data;

      // Step 2: Upload to S3
      const uploadResponse = await fetch(uploadUrl, {
        method: 'PUT',
        body: file,
        headers: { 'Content-Type': file.type }
      });

      if (!uploadResponse.ok) {
        throw new Error('Falha no upload para S3');
      }

      // Step 3: Create map record
      const mapResponse = await mapsAPI.create({
        area: areaId,
        name: newMapName.trim(),
        fileName: file.name
      });

      onMapCreated(mapResponse.data.data);
      setNewMapName('');
      
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Erro ao criar mapa: ' + error.message);
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteMap = async (mapId) => {
    if (!window.confirm('Tem certeza que deseja excluir este mapa?')) return;
    
    try {
      await mapsAPI.delete(mapId);
      onMapDeleted(mapId);
    } catch (error) {
      toast.error('Erro ao excluir mapa: ' + error.message);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Gerenciar Mapas</DialogTitle>
      <DialogContent>
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Criar Novo Mapa
          </Typography>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Nome do Mapa"
                value={newMapName}
                onChange={(e) => setNewMapName(e.target.value)}
                placeholder="Ex: UTI Ala Norte, Térreo Recepção"
                helperText="Digite qualquer nome - sem limitações"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Button
                variant="contained"
                component="label"
                fullWidth
                startIcon={<UploadIcon />}
                disabled={!newMapName.trim() || uploading}
              >
                {uploading ? 'Criando...' : 'Selecionar Arquivo'}
                <input
                  type="file"
                  hidden
                  accept=".png,.jpg,.jpeg,.svg"
                  onChange={handleFileUpload}
                />
              </Button>
            </Grid>
          </Grid>
        </Box>

        <Divider sx={{ my: 2 }} />

        <Typography variant="h6" gutterBottom>
          Mapas Existentes ({maps.length})
        </Typography>
        
        {maps.length === 0 ? (
          <Alert severity="info">
            Nenhum mapa criado ainda. Crie seu primeiro mapa acima!
          </Alert>
        ) : (
          <List>
            {maps.map((map) => (
              <ListItem key={map.id}>
                <ListItemIcon>
                  <MapIcon />
                </ListItemIcon>
                <ListItemText
                  primary={map.name}
                  secondary={`ID: ${map.area} • Arquivo: ${map.file}`}
                />
                <IconButton
                  color="error"
                  onClick={() => handleDeleteMap(map.id)}
                  title="Excluir mapa"
                >
                  <DeleteIcon />
                </IconButton>
              </ListItem>
            ))}
          </List>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Fechar</Button>
      </DialogActions>
    </Dialog>
  );
};

export default InteractiveMaps;